// apps/web/src/wizard/validation.ts — Phase 4 plan 04-03.
//
// Locale-aware error message mapping. The wizard validates
// locally (hand-rolled helpers in schema.ts) and also receives
// server-side errors on the createReservation failure path. Both
// sources produce i18n keys that this module translates via the
// active i18n instance — the components NEVER inline the
// translated strings so adding a third language is a pure
// translation-table change.
//
// Per Pitfall (PLAN §key_links): "never inline the message text in
// the component". Every error display reads either:
//   1. `error.extensions.messageKey` (server-supplied) → t(key)
//   2. a fallback `code` → mapped key (see `mapErrorCode`)
//   3. the generic network error key when neither is present.

import type { TFunction } from "i18next";

import type { ValidationIssue, ValidationResult } from "./schema";

export function firstIssueForPath(
  result: ValidationResult,
  path: string,
): ValidationIssue | undefined {
  return result.issues.find((i) => i.path === path);
}

export function issuesToMessages(
  result: ValidationResult,
  t: TFunction,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of result.issues) {
    // The first issue for a given path wins — subsequent issues
    // on the same path would only confuse the user.
    if (out[issue.path] === undefined) {
      out[issue.path] = t(issue.key);
    }
  }
  return out;
}

/**
 * Maps a server-side error code (GraphQL `extensions.code`) to an
 * i18n key. The mapping mirrors the keys declared in the plan's
 * "i18n bundles" section: `errors.slotFull`, `errors.turnstileFailed`,
 * `errors.reservationExpired`, `errors.noContactMethod`,
 * `errors.areaDisabled`, `errors.outsideAdvanceWindow`,
 * `errors.outsideSameDayCutoff`. Unknown codes fall through to
 * `errors.network` (the "try again" surface).
 */
export function mapErrorCode(code: string | null | undefined): string {
  if (!code) return "errors.network";
  switch (code) {
    case "SLOT_FULL":
      return "errors.slotFull";
    case "TURNSTILE_FAILED":
      return "errors.turnstileFailed";
    case "RESERVATION_EXPIRED":
      return "errors.reservationExpired";
    case "NO_CONTACT_METHOD":
      return "errors.noContactMethod";
    case "AREA_DISABLED":
      return "errors.areaDisabled";
    case "OUTSIDE_ADVANCE_WINDOW":
      return "errors.outsideAdvanceWindow";
    case "OUTSIDE_SAME_DAY_CUTOFF":
      // D-13: same code, two sources (wizard vs self-service). The
      // wizard copy is the same in both locales, so a single key
      // suffices. The self-service surface (04-04) overrides via
      // the `messageKey` field on the server extension.
      return "errors.outsideSameDayCutoff";
    case "INVALID_INPUT":
      // Defense-in-depth: the server's `messageKey` extension
      // already maps to `errors.invalidInput`, but if a future
      // resolver ever omits the messageKey, the code-based fallback
      // here ensures the user still sees the localized copy instead
      // of the raw `errors.invalidInput` literal.
      return "errors.invalidInput";
    case "NETWORK_ERROR":
      return "errors.network";
    default:
      return "errors.network";
  }
}

/**
 * Read a server extension's messageKey first, fall back to the
 * code-based mapping. Always returns an i18n key — never a raw
 * server string.
 */
export function localizeError(
  error:
    { extensions?: { code?: string; messageKey?: string } } | null | undefined,
  t: TFunction,
): string {
  const key =
    error?.extensions?.messageKey ?? mapErrorCode(error?.extensions?.code);
  return t(key);
}
